import { SidePanelModal } from './SidePanelModal';
import React, {
  CSSProperties,
  RefObject,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useWixPatternsContainer } from '@wix/bex-core/react';
import { addResizeObserver } from '@wix/bex-core';
import { SidePanelsState } from '../state/SidePanelsState';

export interface WixPatternsModalsContainerProps {
  rootRef?: RefObject<HTMLElement | null | undefined>;
  anchorRef?: RefObject<HTMLElement | null | undefined>;
  zIndex?: CSSProperties['zIndex'];
  state: SidePanelsState;
}

export function WixPatternsModalsContainer(
  props: WixPatternsModalsContainerProps,
) {
  const { state, rootRef, anchorRef, zIndex = 1 } = props;

  const { appendModalsToBody, lodash } = useWixPatternsContainer();

  const [render, setRender] = useState<{
    top?: number;
    anchor?: HTMLElement | null;
  }>({});

  useEffect(() => {
    const { container, top } = appendModalsToBody
      ? {
          container: document.body,
          top: 0,
        }
      : {
          container: rootRef?.current ?? document.body,
          top: null,
        };

    const setTop = lodash.throttle(() => {
      requestAnimationFrame(() => {
        setRender({
          top: top ?? container.getBoundingClientRect().top,
          anchor: anchorRef?.current ?? container,
        });
      });
    }, 300);

    const disposers = [addResizeObserver(container, setTop)];

    setTop();

    return () => {
      disposers.forEach((d) => d?.());
    };
  }, []);

  // creates a new stacking context
  const modalsContainerStyle = useMemo(
    () => ({
      position: 'relative' as const,
      zIndex,
    }),
    [zIndex],
  );

  let child = (
    <div style={modalsContainerStyle} data-hook="cairo-modals-container">
      {render.top != null && <SidePanelModal state={state} top={render.top} />}
    </div>
  );

  if (render.anchor == null) {
    return null;
  }

  child = createPortal(child, render.anchor);

  return child;
}
